import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Loader2, Lock, CheckCircle2, XCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import Logo from '../components/Logo';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (!token) {
      setError('Lien invalide. Veuillez refaire une demande de réinitialisation.');
    }
  }, [token]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/reset-password', {
        token: token,
        new_password: formData.password,
      });
      setSuccess(true);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Une erreur est survenue';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Affichage du succès
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-slate-50 flex items-center justify-center p-4">
        <Card className="bg-white border border-slate-100 shadow-xl rounded-xl p-8 w-full max-w-md">
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Mot de passe réinitialisé !
            </h3>
            <p className="text-slate-600 mb-6">
              Votre mot de passe a été modifié avec succès. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
            </p>
            <Button
              onClick={() => navigate('/auth')}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-lg py-5"
              data-testid="go-to-login-btn"
            >
              Se connecter
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Affichage de l'erreur (token invalide/expiré)
  if (error && !token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-slate-50 flex items-center justify-center p-4">
        <Card className="bg-white border border-slate-100 shadow-xl rounded-xl p-8 w-full max-w-md">
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Lien invalide
            </h3>
            <p className="text-slate-600 mb-6">
              {error}
            </p>
            <Button
              onClick={() => navigate('/auth')}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-lg py-5"
              data-testid="back-to-login-btn"
            >
              Retour à la connexion
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Formulaire de réinitialisation
  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-slate-50 flex items-center justify-center p-4">
      <Card className="bg-white border border-slate-100 shadow-xl rounded-xl p-8 w-full max-w-md">
        <div className="space-y-6">
          <div className="text-center">
            <Link to="/" className="inline-flex items-center justify-center mb-4">
              <Logo variant="full" size="h-12" />
            </Link>
            <div className="w-14 h-14 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-7 h-7 text-rose-600" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Nouveau mot de passe
            </h3>
            <p className="text-sm text-slate-500">
              Créez un nouveau mot de passe sécurisé pour votre compte.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password" className="text-slate-700">Nouveau mot de passe</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  placeholder="Minimum 6 caractères"
                  data-testid="new-password-input"
                  className="border-slate-200 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all rounded-md pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-slate-700">Confirmer le mot de passe</Label>
              <div className="relative mt-1">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  minLength={6}
                  placeholder="Répétez le mot de passe"
                  data-testid="confirm-password-input"
                  className="border-slate-200 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all rounded-md pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
              <p className="text-sm text-red-500">Les mots de passe ne correspondent pas</p>
            )}

            <Button
              type="submit"
              disabled={loading || !formData.password || !formData.confirmPassword || formData.password !== formData.confirmPassword}
              data-testid="reset-submit-btn"
              className="w-full bg-rose-600 hover:bg-rose-700 text-white shadow-sm transition-all rounded-lg py-5 disabled:opacity-50"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Réinitialisation...</>
              ) : (
                'Réinitialiser le mot de passe'
              )}
            </Button>
          </form>

          <div className="text-center">
            <Link
              to="/auth"
              className="text-sm text-slate-500 hover:text-rose-600 transition-colors"
            >
              Retour à la connexion
            </Link>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ResetPassword;
